import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ButtonBase from "@mui/material/ButtonBase";
import { alpha, styled, type Theme } from "@mui/material/styles";
import {
  syncTraverserStopPulses,
  traverserStopBoxStyles,
} from "_shared/_views/highlights/traverser-stop-highlight";
import {
  TRAVERSER_STOP_NOTICE_CLASS,
  useTraverserStopHasCounterpart,
} from "_shared/_views/highlights/traverser-stop-scope";
import { useEffect, useRef, useState } from "react";
import { ProposalIssueLevel, type ProposalIssue } from "work/proposals/issues/proposal-issues";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

/**
 * Tall enough for four issues before it scrolls. Past that the list would take its height out of the
 * table or form underneath, which is what the issues are about.
 */
const MAX_VISIBLE_ISSUES_HEIGHT = "8.5rem";

/** One issue's text, and the line it sits on. The short window's cap below is measured in these. */
const ISSUE_FONT_SIZE = "0.8125rem";
const ISSUE_LINE_HEIGHT = 1.4;

/**
 * Tall enough for two one-line issues, on a window too short to spare four.
 *
 * A pane's height is the window's less the app bar, the footer and the page's own sticky header, so
 * four issues on a 1280×720 window — a 1920px monitor at 150% — were 136px of a tab body about 390px
 * tall. A fee schedule raises one issue per category, so its list grew with the table it sits over
 * and left that table room for a single row (#5809). The traverser still steps through every issue,
 * and scrolls the list to the one it stops on.
 *
 * Worked out from the row's own line, padding and gap rather than written as a length, so the list
 * ends on the boundary between the second issue and the third. A round 4.25rem left a 6px sliver of
 * the third along the bottom edge, which read as a drawing fault rather than as more to scroll to.
 */
function shortWindowMaxVisibleIssuesHeight(theme: Theme): string {
  const issueHeight = `(${ISSUE_FONT_SIZE} * ${ISSUE_LINE_HEIGHT} + ${theme.spacing(1)})`;
  return `calc(2 * ${issueHeight} + ${theme.spacing(0.5)})`;
}

/** A window no taller than this is short: 768px and 720px windows are, and an 864px one is not. */
const SHORT_WINDOW_QUERY = "@media (max-height: 50rem)";

const IssueList = styled("ul")(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  gap: theme.spacing(0.5),
  listStyle: "none",
  margin: 0,
  maxHeight: MAX_VISIBLE_ISSUES_HEIGHT,
  minWidth: 0,
  overflowY: "auto",
  padding: 0,
  // The offset parent of its items, so the scroll-into-view below measures them against the list.
  position: "relative",
  width: "100%",
  [SHORT_WINDOW_QUERY]: {
    maxHeight: shortWindowMaxVisibleIssuesHeight(theme),
  },
}));

/**
 * Holds the list and the "more" controls drawn over its edges.
 *
 * Stacked above the content after it, because a control that reaches past the list's edge reaches
 * over that content — and a fee schedule's table box is itself stacked at 1, so at equal height the
 * table, coming later, was drawn over the control. Still well under the page's sticky header.
 */
const IssueListFrame = styled("div")(() => ({
  flexShrink: 0,
  minWidth: 0,
  position: "relative",
  width: "100%",
  zIndex: 2,
}));

/**
 * Says the capped list holds more than it shows, and which way, and steps to the next one hidden.
 *
 * A scrolled-out issue was only ever a scrollbar away, and a scrollbar is no message at all where the
 * system draws it as an overlay that appears on hover — a reviewer read two issues and a cut-off edge
 * as the rest having been dropped (#5809).
 *
 * Both directions sit together across the list's bottom edge, mostly in the gap under the list, and
 * overlap only an issue's padding. Inside the list they covered the end of the last line they could
 * see, and the top edge has nowhere to go: the tab's content box clips at the list's top.
 */
const MoreIssuesControls = styled("div")(({ theme }) => ({
  bottom: 0,
  display: "flex",
  gap: theme.spacing(0.5),
  position: "absolute",
  right: theme.spacing(1.5),
  transform: "translateY(65%)",
  zIndex: 1,
}));

const MoreIssuesButton = styled(ButtonBase)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.shadows[1],
  color: theme.palette.text.secondary,
  display: "flex",
  fontFamily: theme.typography.fontFamily,
  fontSize: "0.75rem",
  gap: theme.spacing(0.25),
  lineHeight: 1,
  padding: theme.spacing(0.25, 1, 0.25, 0.5),
  "& .MuiSvgIcon-root": {
    fontSize: "1rem",
  },
  "&:hover": {
    color: theme.palette.text.primary,
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 1,
  },
}));

const IssueListItem = styled("li")(() => ({
  display: "flex",
  minWidth: 0,
}));

function issueRowStyles(theme: Theme, level: ProposalIssueLevel, lit: boolean) {
  const severity = level === ProposalIssueLevel.Critical ? theme.palette.error : theme.palette.warning;
  return {
    alignItems: "flex-start",
    // Lit, it drops its severity wash for the page's own surface, so the stop's blue tint over it is
    // the same blue as over the row's white cells rather than a grey mixed with the orange.
    backgroundColor: lit ? theme.palette.background.paper : alpha(severity.main, 0.06),
    borderLeft: `3px solid ${severity.main}`,
    borderRadius: theme.shape.borderRadius,
    boxSizing: "border-box" as const,
    color: theme.palette.text.primary,
    display: "flex",
    flex: 1,
    fontSize: ISSUE_FONT_SIZE,
    gap: theme.spacing(0.75),
    justifyContent: "flex-start",
    lineHeight: ISSUE_LINE_HEIGHT,
    minWidth: 0,
    padding: theme.spacing(0.5, 1),
    textAlign: "left" as const,
    // The same tint and pulse a lit row takes — see `traverser-stop-highlight`. The severity stays
    // with the edge and the icon.
    ...(lit && traverserStopBoxStyles),
    "& .MuiSvgIcon-root": {
      color: severity.main,
      flexShrink: 0,
      fontSize: "1rem",
      // Centres the icon on the first line of text, not on the whole wrapped block.
      marginTop: "0.05rem",
    },
  };
}

const StaticIssue = styled("div", {
  shouldForwardProp: (prop) => prop !== "level" && prop !== "lit",
})<{ level: ProposalIssueLevel; lit: boolean }>(({ theme, level, lit }) => issueRowStyles(theme, level, lit));

const SelectableIssue = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "level" && prop !== "lit",
})<{ level: ProposalIssueLevel; lit: boolean }>(({ theme, level, lit }) => ({
  ...issueRowStyles(theme, level, lit),
  fontFamily: theme.typography.fontFamily,
  "&:hover": {
    backgroundColor: alpha(
      level === ProposalIssueLevel.Critical ? theme.palette.error.main : theme.palette.warning.main,
      0.14,
    ),
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

type ProposalIssueListProps = {
  issues: ProposalIssue[];
  /**
   * The field the traverser is on. The issues about it are marked current, and lit only while the
   * stop lights nothing else in the tab.
   */
  activeReviewField?: ProposalField;
  /**
   * Moves the page to the field an issue is about — the same stop "Prev / Next Issue" would make — and,
   * for an issue about one part of it, to that part (#6097). Omitted, the issues are read-only text.
   */
  onIssueSelected?: (issue: ProposalIssue) => void;
  className?: string;
};

/**
 * A tab's issues, one per line, each marked with the severity the tab's badge counts it under.
 *
 * A stop lights one thing. When it lights a row or field of its own in the tab, that is the stop's
 * highlight and the issue stays unlit; only a stop with nothing else to light — the whole fee
 * schedule, the whole conflicts collection, a draft's vendor team, a row the search box hides — lights
 * its issue instead. The issue and its row used to light together, and a reader counting lit items
 * counted two pending issues on a tab that had one (#5649). The issue is still marked current either
 * way, and clicking an issue makes the same stop the traverser would.
 */
export default function ProposalIssueList(props: Readonly<ProposalIssueListProps>) {
  const { issues, activeReviewField, onIssueSelected, className } = props;
  const listRef = useRef<HTMLUListElement>(null);
  const activeIndex = issues.findIndex((issue) => issue.field.isEqualTo(activeReviewField));
  const stopHasCounterpart = useTraverserStopHasCounterpart();
  const litFieldKey = activeIndex >= 0 && !stopHasCounterpart ? issues[activeIndex].field.cacheKey : undefined;

  // A lit issue starts pulsing on its own commit; put it on the beat of any other stop pulse drawn.
  useEffect(() => {
    if (litFieldKey !== undefined) syncTraverserStopPulses();
  }, [litFieldKey]);

  // A stop on an issue scrolled out of the capped list scrolls it back in, within the list only.
  useEffect(() => {
    const list = listRef.current;
    if (!list || activeIndex < 0) return;
    const item = list.children.item(activeIndex);
    if (!(item instanceof HTMLElement)) return;
    if (item.offsetTop < list.scrollTop) {
      list.scrollTop = item.offsetTop;
    } else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }, [activeIndex]);

  // How many issues sit wholly or partly outside the list's box, above and below. Measured, so it
  // waits for a renderer that lays out: jsdom has no ResizeObserver, and draws no "more" controls.
  const [outOfView, setOutOfView] = useState<IssuesOutOfView>(NONE_OUT_OF_VIEW);
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === "undefined") return;
    // Reports once on `observe`, and again whenever the list or any issue in it changes size. Keyed
    // on the issues, so a new item is observed as well — an item added past the cap leaves the list's
    // own box exactly as it was.
    const observer = new ResizeObserver(() => setOutOfView(measureIssuesOutOfView(list)));
    observer.observe(list);
    for (const item of list.children) observer.observe(item);
    return () => observer.disconnect();
  }, [issues]);

  function revealNextIssue(direction: "up" | "down") {
    const list = listRef.current;
    if (!list) return;
    const items = [...list.children].filter((item): item is HTMLElement => item instanceof HTMLElement);
    if (direction === "down") {
      const next = items.find((item) => item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight + 1);
      if (next) list.scrollTop = next.offsetTop + next.offsetHeight - list.clientHeight;
    } else {
      const previous = items.findLast((item) => item.offsetTop < list.scrollTop - 1);
      if (previous) list.scrollTop = previous.offsetTop;
    }
  }

  if (issues.length === 0) return null;

  return (
    // The caller's margins and insets go on the frame, so its edges are the list's edges and the
    // "more" controls straddle the list rather than the margin under it.
    <IssueListFrame className={className}>
      <IssueList
        ref={listRef}
        aria-label="Issues"
        onScroll={(event) => setOutOfView(measureIssuesOutOfView(event.currentTarget))}
      >
        {issues.map((issue, index) => {
          const active = issue.field.isEqualTo(activeReviewField);
          const lit = active && !stopHasCounterpart;
          const className = lit ? TRAVERSER_STOP_NOTICE_CLASS : undefined;
          const icon =
            issue.level === ProposalIssueLevel.Critical ? (
              <ErrorOutlinedIcon titleAccess="Blocker" />
            ) : (
              <WarningAmberIcon titleAccess="Warning" />
            );
          // One field can carry more than one issue, so the field alone is not a unique key.
          const key = `${issue.field.cacheKey}-${index}`;

          return (
            <IssueListItem key={key} aria-current={active ? "true" : undefined}>
              {onIssueSelected ? (
                <SelectableIssue
                  level={issue.level}
                  lit={lit}
                  className={className}
                  onClick={() => onIssueSelected(issue)}
                >
                  {icon}
                  <span>{issue.description}</span>
                </SelectableIssue>
              ) : (
                <StaticIssue level={issue.level} lit={lit} className={className}>
                  {icon}
                  <span>{issue.description}</span>
                </StaticIssue>
              )}
            </IssueListItem>
          );
        })}
      </IssueList>
      {(outOfView.above > 0 || outOfView.below > 0) && (
        <MoreIssuesControls>
          {outOfView.above > 0 && (
            <MoreIssuesButton
              aria-label={moreIssuesLabel(outOfView.above, "above")}
              onClick={() => revealNextIssue("up")}
            >
              <KeyboardArrowUpIcon />
              {outOfView.above} more
            </MoreIssuesButton>
          )}
          {outOfView.below > 0 && (
            <MoreIssuesButton
              aria-label={moreIssuesLabel(outOfView.below, "below")}
              onClick={() => revealNextIssue("down")}
            >
              <KeyboardArrowDownIcon />
              {outOfView.below} more
            </MoreIssuesButton>
          )}
        </MoreIssuesControls>
      )}
    </IssueListFrame>
  );
}

type IssuesOutOfView = { above: number; below: number };
const NONE_OUT_OF_VIEW: IssuesOutOfView = { above: 0, below: 0 };

/**
 * The issues the list's box does not wholly show. A pixel of slack, because a scroll position and a
 * layout box can disagree by a subpixel and an issue that is on screen is not "more".
 */
function measureIssuesOutOfView(list: HTMLElement): IssuesOutOfView {
  const top = list.scrollTop;
  const bottom = list.scrollTop + list.clientHeight;
  let above = 0;
  let below = 0;
  for (const item of list.children) {
    if (!(item instanceof HTMLElement)) continue;
    if (item.offsetTop < top - 1) above += 1;
    else if (item.offsetTop + item.offsetHeight > bottom + 1) below += 1;
  }
  // The same state object while nothing changed, so a scroll inside one issue renders nothing.
  return above === 0 && below === 0 ? NONE_OUT_OF_VIEW : { above, below };
}

function moreIssuesLabel(count: number, where: "above" | "below"): string {
  return `${count} more ${count === 1 ? "issue" : "issues"} ${where}`;
}
